import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgForOf, NgIf } from "@angular/common";
import { PaginatorModule, PaginatorState } from "@openng/optimus-ui/paginator";
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import {
  ICampaign,
  ICampaignData,
  ICampaignPhase,
  IConnectTemplateToPhasesRequest
} from '../../../../models/campaign/campaign.interface';
import { catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CampaignService } from '../../../../services/campaign.service';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { CheckboxModule, CheckboxChangeEvent } from '@openng/optimus-ui/checkbox';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import { ToastBarService } from '../../../../services/toast-bar.service';

import { TypeEnum } from '../../../../models/template/type.enum';
import { forkJoin, Observable, of } from 'rxjs';

interface CampaignWithSelection extends ICampaign {
  selectedPhaseIds: string[];
}

@Component({
    selector: 'app-edit-template-to-phase-connections',
    imports: [
        NgForOf,
        NgIf,
        PaginatorModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogContent,
        CheckboxModule,
        AccordionModule
    ],
    templateUrl: './edit-template-to-phase-connections.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './edit-template-to-phase-connections.component.scss'
})
export class EditTemplateToPhaseConnections implements OnInit {
  // Full list for the current search term; `campaigns` is the current page slice.
  private allCampaigns: CampaignWithSelection[] = [];
  campaigns: CampaignWithSelection[] = [];

  // Phases pre-selected per campaign (from getCampaignListForTemplate).
  private selectionsMap = new Map<string, string[]>();

  first: number = 0;
  rows: number = 10;
  totalRecords = 0;

  searchControl = new FormControl('');

  constructor(
    protected campaignService: CampaignService,
    protected toastBarService: ToastBarService,
    @Inject(MAT_DIALOG_DATA) public data: {
      templateId: string;
      templateType: TypeEnum;
    },
    private dialogRef: MatDialogRef<EditTemplateToPhaseConnections>
  ) {
  }

  ngOnInit(): void {
    // Search sa debounce
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.first = 0;
        this.loadCampaignsWithSelections();
      });

    this.loadCampaignsWithSelections();
  }

  private loadCampaignsWithSelections(): void {
    // First, get the campaigns that have this template (to know which phases to pre-select)
    this.campaignService.getCampaignListForTemplate(this.data.templateId).subscribe({
      next: (templateCampaigns: any) => {
        // Store which phases should be selected for each campaign
        this.selectionsMap = new Map<string, string[]>();
        templateCampaigns.forEach((campaignData: ICampaignData) => {
          if (campaignData.campaignId && campaignData.phases) {
            const phaseIds = campaignData.phases
              .map(phase => phase.phaseId)
              .filter((id): id is string => !!id);
            this.selectionsMap.set(campaignData.campaignId, phaseIds);
          }
        });

        // Then load all campaigns and apply the selections
        this.loadCampaigns();
      },
      error: (err: any) => {
        console.error("Error loading campaigns for template", err);
        // Still load campaigns even if we can't get selections
        this.selectionsMap = new Map();
        this.loadCampaigns();
      }
    });
  }

  onPageChange(event: PaginatorState) {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 10;

    this.updateDisplayedCampaigns();
  }

  protected loadCampaigns(): void {
    // Fetch everything for the current search term once and paginate client-side.
    let params: { first: number, rows: number, search?: string } = {
      first: 0,
      rows: 100000,
    };
    const search = this.searchControl.value ?? '';
    if (search) {
      params.search = search;
    }

    this.campaignService.getCampaignList(params).subscribe({
      next: (res: any) => {
        // Map campaigns with their selections from the API
        this.allCampaigns = res.map((campaign: ICampaign) => ({
          ...campaign,
          selectedPhaseIds: this.selectionsMap.get(campaign.campaignId!) || []
        }));

        // Show campaigns that already have selected phases first.
        this.allCampaigns.sort((a, b) =>
          (b.selectedPhaseIds.length > 0 ? 1 : 0) - (a.selectedPhaseIds.length > 0 ? 1 : 0)
        );

        this.totalRecords = this.allCampaigns.length;
        this.updateDisplayedCampaigns();
      },
      error: (err: any) => {
        console.error('Error loading campaigns', err);
      }
    });
  }

  private updateDisplayedCampaigns(): void {
    this.campaigns = this.allCampaigns.slice(this.first, this.first + this.rows);
  }

  onSubmit() {
    // Collect all selected phases from all campaigns
    const requests = [];

    if (!this.allCampaigns || this.allCampaigns.length === 0) {
      return;
    }

    for (const campaign of this.allCampaigns) {
      const request = {
        templateId: this.data.templateId,
        campaignId: campaign.campaignId!,
        selectedPhaseIds: campaign.selectedPhaseIds,
        templateType: this.data.templateType.toString()
      }

      const observable = this.campaignService.editTemplateToPhaseConnections(request).pipe(
        catchError((err) => {
          console.error(`Failed to connect campaign ${campaign.campaignId}`, err);
          this.toastBarService.showToastError("Something went wrong while editing connection!")
          return of({campaignId: campaign.campaignId, error: err});
        })
      );
      requests.push(observable);
    }

    forkJoin(requests).subscribe(
      {
        next: (results: any) => {
          const anyFailed = results.some((r: any) => r && typeof r === 'object' && 'error' in r);
          if (!anyFailed) {
            this.toastBarService.showToast({
              severity: 'success',
              summary: 'Success',
              detail: `Successfully edited template connection to phases.`,
            });
          }
          this.closeDialog();
        },
      });
  }

  isAllPhasesSelected(campaign: CampaignWithSelection): boolean {
    if (!campaign.phases || campaign.phases.length === 0) return false;
    return campaign.phases.every(phase => phase.phaseId && campaign.selectedPhaseIds.includes(phase.phaseId));
  }

  toggleAllPhases(campaign: CampaignWithSelection, event: CheckboxChangeEvent) {
    const isChecked = event.checked;

    if (isChecked) {
      campaign.selectedPhaseIds = campaign.phases
        ?.map(phase => phase.phaseId!)
        .filter((id): id is string => !!id) || [];
    } else {
      campaign.selectedPhaseIds = [];
    }
  }

  protected closeDialog() {
    this.dialogRef.close();
  }

  getPhaseCount(campaign: CampaignWithSelection): number {
    return campaign.phases?.length || 0;
  }

  getSelectedPhaseCount(campaign: CampaignWithSelection): number {
    if (!campaign.phases || !campaign.selectedPhaseIds) return 0;
    return campaign.phases.filter(
      phase => phase.phaseId && campaign.selectedPhaseIds.includes(phase.phaseId)
    ).length;
  }
}
